import React, {
  useDeferredValue,
  useEffect,
  useState,
  useRef,
  type FC,
  type HTMLAttributes,
} from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { RouterLink } from 'src/components/router-link';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { DatePicker } from '@mui/x-date-pickers';
import { formatSSNPartial, maskSSNForDisplay, maskSSNForInputDisplay } from 'src/utils/ssn-mask';
import { PriorMedicareCharges, PriorMedicareChargesRef } from './prior-medicare-charges';
import { useDispatch, useSelector } from 'react-redux';
import {
  createPatientAction,
  editPatientAction,
  getAllPatientsListResetAction,
  getAllPhysicians,
  getAllTherapistAction,
  getPatientIdAction,
  getSinglePatient,
  resetCreatePatientAction,
  resetEditPatientAction,
} from 'src/redux/actions/patientAction';
import { useLocation, useNavigate } from 'react-router';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { paths } from 'src/paths';
import {
  allowedCharactersRegexWithRegex,
  commonValidation,
} from 'src/pages/details-page/validations/customValidation';
import moment from 'moment';
import { patientFormTestId, patientsOptionTestId } from 'src/sections/dashboard/patients/test-ids';

interface CustomerEditFormProps {
  action: string;
  file: any;
}

interface CategoryOption {
  label: string;
  value: string;
}

const categoryOptions: CategoryOption[] = [
  {
    label: 'Male',
    value: 'MALE',
  },
  {
    label: 'Female',
    value: 'FEMALE',
  },
];

export const formatContactNumber = (value: string) => {
  if (value === null) {
    return {
      formattedValue: '',
      cleanedValue: '',
    };
  }

  const cleanedValue = value?.replace(/[^\d+]/g, '');

  const pattern = /^(\+\d{1,3})?(\d{3})?(\d{3})(\d{4})$/;

  const formattedValue = cleanedValue?.replace(pattern, (match, p1, p2, p3, p4) => {
    if (p2 && p3) {
      if (p1) {
        return `${p1} (${p2})-${p3}-${p4}`;
      } else {
        return `(${p2})-${p3}-${p4}`;
      }
    } else {
      return cleanedValue;
    }
  });

  return {
    formattedValue,
    cleanedValue,
  };
};

function getCurrentDateUTCWithoutTime(date: any) {
  // Get the date components
  const utcDateFormate = moment(date).format('YYYY-MM-DD');
  // Construct the UTC date string with time portion set to 00:00:00.000Z
  const utcDateString = `${utcDateFormate}T00:00:00.000Z`;

  return utcDateString;
}

export const CustomerEditForm: FC<CustomerEditFormProps> = (props) => {
  const { action, file } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const patientId = state?.patientId;
  const cameFromBanner = state?.returnTo?.label === 'Episode Of Care Details';
  const [search, setSearch] = useState('');
  const [timer, setTimer] = useState<any>(undefined);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Ref to access prior charges component methods
  const priorChargesRef = useRef<PriorMedicareChargesRef>(null);

  // Helper function to check if a required field should show error styling
  const shouldShowFieldError = (fieldName: string, fieldValue: any, fieldError: any) => {
    // Show error if field was touched and has error
    if ((formik.touched as any)[fieldName] && fieldError) return true;

    // Show error if form validation was triggered and has error
    if (showValidationErrors && fieldError) return true;

    // Show error immediately for empty required fields when coming from banner
    if (cameFromBanner && action === 'Edit') {
      if (fieldName === 'primaryPhysician' && !fieldValue) return true;
      if (fieldName === 'dateOfBirth' && !fieldValue) return true;
    }

    return false;
  };

  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const createPatientData = useSelector((state: any) => state?.createPatientReducer);
  const editPatientData = useSelector((state: any) => state?.editPatientReducer);
  const allTherapistDisciplineResponse = useSelector(
    (state: any) => state?.getAllTherapistDisciplineReducer
  );
  const physicianData = useSelector(
    (state: any) => state?.getEnterprisePhysicianReducer?.enterprisePhysician?.content
  );
  const [pageNo, setPageNo] = useState(0);

  const handleSearchInputChange = (e: any) => {
    if (timer) {
      clearTimeout(timer);
    }
    setPageNo(0);
    const timeout = setTimeout(() => {
      setSearch(e.target.value);
    }, 1000);

    setTimer(timeout);
  };

  const [physicianList, setPhysicianList] = useState<any>([]);
  const [selectedPhysician, setSelectedPhysician] = useState<any>(null);

  const physicianResponse = useSelector((state: any) => state?.getEnterprisePhysicianReducer);

  useEffect(() => {
    dispatch(getAllTherapistAction());
  }, []);

  const [patientError, setPatientError] = useState('');
  const [showSSN, setShowSSN] = useState(false);
  const [editSSNFocused, setEditSSNFocused] = useState(false);
  const { patients, patientDetails } = paths;
  const facilityId = localStorage.getItem('facilityId');

  useEffect(() => {
    if (!Array.isArray(physicianData)) return;

    setPhysicianList((prev: any) => {
      const selectedPhysicianUuid = formik.values.primaryPhysician;
      let updatedList: any[] = [];

      if (pageNo === 0) {
        // When pageNo is 0, replace the list with new data (search results or initial load)
        if (search !== '') {
          // User is searching - show only search results (don't add selected value if search has no results)
          updatedList = [...physicianData];

          // If selected physician is in search results, update the selectedPhysician object
          if (selectedPhysicianUuid) {
            const foundPhysician = physicianData.find(
              (physician: any) => physician.uuid === selectedPhysicianUuid
            );
            if (foundPhysician) {
              setSelectedPhysician(foundPhysician);
            }
          }
        } else {
          // Normal load (no search) - replace with new data
          updatedList = [...physicianData];

          // If we have a selected physician, ensure it's in the list (only when not searching)
          if (selectedPhysicianUuid) {
            // Check if selected physician is in the new data
            const isSelectedInNewData = physicianData.some(
              (physician: any) => physician.uuid === selectedPhysicianUuid
            );

            if (!isSelectedInNewData) {
              if (selectedPhysician) {
                updatedList = [selectedPhysician, ...updatedList];
              } else {
                const foundInPrev = prev?.find(
                  (physician: any) => physician.uuid === selectedPhysicianUuid
                );
                if (foundInPrev) {
                  updatedList = [foundInPrev, ...updatedList];
                  setSelectedPhysician(foundInPrev);
                }
              }
            } else {
              // Selected physician is in new data, update selectedPhysician object
              const foundPhysician = physicianData.find(
                (physician: any) => physician.uuid === selectedPhysicianUuid
              );
              if (foundPhysician) {
                setSelectedPhysician(foundPhysician);
              }
            }
          }
        }
      } else {
        // Pagination - append to existing list
        const existingUuids = new Set(prev?.map((p: any) => p.uuid) || []);
        const newPhysicians = physicianData.filter(
          (physician: any) => !existingUuids.has(physician.uuid)
        );
        updatedList = [...(prev || []), ...newPhysicians];
      }

      return updatedList;
    });
  }, [physicianData, search, pageNo]);

  const loaderMorePhysician = () => {
    if (!physicianResponse?.enterprisePhysician?.last && search === '') {
      setPageNo((prev) => prev + 1);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    dispatch(
      getAllPhysicians({
        facilityId: facilityId,
        date: today,
        pageSize: 30,
        page: pageNo,
        search: search,
      })
    );
  }, [search, pageNo]);
  const saveToLocalStorage = (key: any, value: any) => {
    if (value !== undefined && value !== null) {
      localStorage.setItem(key, JSON.stringify(value));
    }
  };

  const getFromLocalStorage = (key: any) => {
    const storedValue = localStorage.getItem(key);

    if (storedValue !== undefined && storedValue !== null && storedValue !== 'undefined') {
      try {
        return JSON.parse(storedValue);
      } catch (error) {
        console.error(`Error parsing JSON for key '${key}':`, error);
        // Clear the corrupted value
        localStorage.removeItem(key);
        return null;
      }
    }
    return null;
  };
  useEffect(() => {
    if (patientData) {
      saveToLocalStorage('gender', patientData?.gender);
      if (patientData?.physicianEntitySet && patientData?.physicianEntitySet[0]) {
        saveToLocalStorage('primaryPhysician', patientData?.physicianEntitySet[0]);
        setSelectedPhysician(patientData?.physicianEntitySet[0]);
      }
    }
  }, [patientData]);

  const removeDashes = (ssn: any) => {
    return ssn ? ssn.replace(/-/g, '') : '';
  };

  const initiaGenderValue = getFromLocalStorage('gender') || '';
  const dobWithoutTimeZone = patientData?.dateOfBirth?.split('T')?.at(0);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: action === 'Create' ? '' : patientData?.firstName?.trim(),
      lastName: action === 'Edit' ? patientData?.lastName?.trim() : '',
      dateOfBirth:
        action === 'Edit'
          ? patientData?.dateOfBirth
            ? new Date(
                new Date(dobWithoutTimeZone).valueOf() +
                  new Date(dobWithoutTimeZone).getTimezoneOffset() * 60 * 1000
              )
            : ''
          : null,
      gender: action === 'Edit' ? initiaGenderValue : '',
      primaryPhysician:
        action === 'Edit'
          ? patientData?.physicianEntitySet?.length > 0
            ? patientData?.physicianEntitySet[0]?.uuid
            : ''
          : '',
      otTherapist: action === 'Edit' ? patientData?.otTherapist : '',
      ptTherapist: action === 'Edit' ? patientData?.ptTherapist : '',
      slpTherapist: action === 'Edit' ? patientData?.slpTherapist : '',
      medicalRecordNumber: action === 'Edit' ? patientData?.medicalRecordNumber?.trim() : '',
      bedAssignment: action === 'Edit' ? patientData?.bedAssignment?.trim() : '',
      socialSecurityNumber: '',
      roomNumber: action === 'Edit' ? removeDashes(patientData?.roomNumber?.trim()) : '',
      floorNumber: action === 'Edit' ? removeDashes(patientData?.floorNumber?.trim()) : '',
      patientEmail: action === 'Create' ? '' : patientData?.patientEmail?.trim(),
      patientPhoneNo:
        action === 'Create'
          ? ''
          : formatContactNumber(patientData?.patientPhoneNo?.trim()).formattedValue,
      priorCharges: [],
    },
    validationSchema: Yup.object({
      firstName: commonValidation.customValidation(
        'First Name',
        true,
        true,
        false,
        allowedCharactersRegexWithRegex,
        2,
        64
      ),
      lastName: commonValidation.customValidation(
        'Last Name',
        true,
        true,
        false,
        allowedCharactersRegexWithRegex,
        2,
        64
      ),
      dateOfBirth: Yup.date().required('Please select the Date Of Birth'),
      gender: Yup.string().required('Please select the Gender'),
      primaryPhysician: Yup.string().max(255).required('Please select the Primary Physician'),
      otTherapist: Yup.string().nullable(),
      ptTherapist: Yup.string().nullable(),
      slpTherapist: Yup.string().nullable(),
      medicalRecordNumber: Yup.string()
        .required('Please enter a valid Medical Record Number')
        .matches(
          /^[a-zA-Z0-9-]+$/,
          'Medical Record Number must contain only alphanumeric characters and the - character'
        ),

      bedAssignment: Yup.string().max(255),
      socialSecurityNumber: Yup.string()
        // Validate the cleaned digits only; any mask/format chars (dashes, X) are stripped first
        // so a masked display value can never block the save (RA-1948).
        .transform((value) => (value ? value.replace(/\D/g, '') : value))
        .matches(/^[0-9]+$/, {
          excludeEmptyString: true,
          message: 'Social Security Number must contain only numeric digits',
        })
        .min(9, 'Social Security Number must be equal to 9')
        .max(9, 'Social Security Number must be at most 9 characters'),

      roomNumber: Yup.string().matches(
        /^[0-9a-zA-Z]+$/,
        'Room Number must contain only numbers and letters'
      ),
      floorNumber: Yup.string().matches(
        /^[0-9a-zA-Z ]+$/,
        'Floor Number must contain only alphanumeric characters'
      ),
      patientEmail: Yup.string().email('Must be a valid email').max(255),
      patientPhoneNo: Yup.string().matches(/^(\+\d{1,3})?\s?\(?\d{3}\)?-?\s?\d{3}-?\d{4}$/, {
        excludeEmptyString: true,
        message: 'Please Enter a valid phone Number',
      }),
      priorCharges: Yup.array()
        // Allow empty array - no prior charges required for new patients
        .of(
          Yup.object().shape({
            year: Yup.number()
              .required('Year is required')
              .min(2024, 'Year must be 2024 or later')
              .max(
                new Date().getFullYear(),
                `Year cannot be greater than ${new Date().getFullYear()}`
              ),
            fromDate: Yup.date()
              .required('From Date is required')
              .typeError('From Date is required')
              .test('required', 'From Date is required', function (value) {
                return value !== null && value !== undefined;
              }),
            toDate: Yup.date()
              .required('To Date is required')
              .typeError('To Date is required')
              .test('required', 'To Date is required', function (value) {
                return value !== null && value !== undefined;
              })
              .when('fromDate', {
                is: (fromDate: Date | null) => !!fromDate,
                then: (schema) =>
                  schema.min(
                    Yup.ref('fromDate'),
                    'To Date must be greater than or equal to From Date'
                  ),
              }),
            ptSlpCharges: Yup.string().test(
              'is-number',
              'PT/SLP must be a valid number',
              (value) => {
                // Allow empty values
                if (!value || value === '') return true;
                const num = parseFloat(value);
                return !isNaN(num) && num >= 0;
              }
            ),
            otCharges: Yup.string().test('is-number', 'OT must be a valid number', (value) => {
              // Allow empty values
              if (!value || value === '') return true;
              const num = parseFloat(value);
              return !isNaN(num) && num >= 0;
            }),
          })
        ),
    }),
    onSubmit: async (values, helpers): Promise<void> => {
      setShowValidationErrors(true);

      // CRITICAL: Ensure priorCharges are synced to formik BEFORE validation
      // Get current values from component (source of truth)
      let currentPriorCharges: any = [];
      if (priorChargesRef.current) {
        currentPriorCharges = priorChargesRef.current.getPriorCharges();
      } else {
        currentPriorCharges = values.priorCharges || [];
      }

      // Set values in formik - this must happen before validation
      helpers.setFieldValue('priorCharges', currentPriorCharges as any, false);
      // Wait for formik to sync the values - ensure sync is complete
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Validate the entire form including priorCharges - this validates ALL fields at once
      // validateForm() validates all fields and returns ALL errors
      let errors = await helpers.validateForm();

      // Force validation of priorCharges field to ensure all nested errors are caught
      helpers.validateField('priorCharges');
      // Wait for validation to complete
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Re-validate to get ALL errors including priorCharges - this ensures we catch everything
      const finalErrors = await helpers.validateForm();
      // Merge all errors - ensure we have complete error set
      errors = { ...errors, ...finalErrors };

      // CRITICAL: Also check formik.errors directly - this is the source of truth after validation
      // Formik.errors contains all validation errors after validateForm/validateField
      const formikPriorChargesErrors = formik.errors.priorCharges;
      if (formikPriorChargesErrors) {
        errors.priorCharges = formikPriorChargesErrors;
      } else if (finalErrors.priorCharges) {
        errors.priorCharges = finalErrors.priorCharges;
      }

      // Additional validation for date overlaps - use CURRENT values from component
      if (
        currentPriorCharges &&
        Array.isArray(currentPriorCharges) &&
        currentPriorCharges.length > 0
      ) {
        const overlapErrors: any = {};
        let hasOverlaps = false;

        for (let i = 0; i < currentPriorCharges.length; i++) {
          const charge1 = currentPriorCharges[i] as any;
          if (!charge1?.fromDate || !charge1?.toDate) continue;

          for (let j = i + 1; j < currentPriorCharges.length; j++) {
            const charge2 = currentPriorCharges[j] as any;
            if (!charge2?.fromDate || !charge2?.toDate) continue;

            // Check if date ranges overlap
            if (charge1.fromDate <= charge2.toDate && charge2.fromDate <= charge1.toDate) {
              hasOverlaps = true;
              if (!overlapErrors[i]) overlapErrors[i] = {};
              if (!overlapErrors[j]) overlapErrors[j] = {};
              overlapErrors[i].dateOverlap = 'Date range overlaps with another entry';
              overlapErrors[j].dateOverlap = 'Date range overlaps with another entry';
            }
          }
        }

        if (hasOverlaps) {
          // Merge overlap errors with existing Yup errors
          const priorChargesErrors: any = Array.isArray(errors.priorCharges)
            ? [...errors.priorCharges]
            : [];
          Object.keys(overlapErrors).forEach((index) => {
            const idx = parseInt(index, 10);
            if (!priorChargesErrors[idx]) priorChargesErrors[idx] = {};
            priorChargesErrors[idx] = { ...priorChargesErrors[idx], ...overlapErrors[idx] };
          });
          errors.priorCharges = priorChargesErrors;
          helpers.setFieldError('priorCharges', priorChargesErrors);
        }
      }

      // Check if there are any errors, including nested errors in priorCharges array
      // Also check formik.errors directly as it's the source of truth
      const hasErrors = (() => {
        // Check top-level errors (excluding priorCharges)
        const topLevelErrors = Object.keys(errors).filter((key) => key !== 'priorCharges');
        if (topLevelErrors.length > 0) return true;

        // Check formik.errors.priorCharges directly
        const priorChargesErrors = formik.errors.priorCharges || errors.priorCharges;

        if (priorChargesErrors) {
          if (Array.isArray(priorChargesErrors)) {
            // Check if any item in the array has errors
            const hasArrayErrors = priorChargesErrors.some((error: any) => {
              if (!error) return false;
              if (typeof error === 'string') return true;
              if (typeof error === 'object') {
                return Object.keys(error).length > 0;
              }
              return false;
            });
            if (hasArrayErrors) return true;
          } else if (typeof priorChargesErrors === 'object') {
            // Check if object has any error properties
            const errorKeys = Object.keys(priorChargesErrors);
            if (errorKeys.length > 0) {
              // Check if any of the values are non-empty error objects/strings
              const hasObjectErrors = errorKeys.some((key) => {
                const errorValue = priorChargesErrors[key];
                if (!errorValue) return false;
                if (typeof errorValue === 'string') return true;
                if (typeof errorValue === 'object') {
                  return Object.keys(errorValue).length > 0;
                }
                return false;
              });
              if (hasObjectErrors) return true;
            }
          }
        }

        return false;
      })();

      if (hasErrors) {
        // Set errors in formik to ensure they're displayed
        if (errors.priorCharges) {
          helpers.setFieldError('priorCharges', errors.priorCharges as any);
        }
        // Find first error field and focus it
        const errorFields = Object.keys(errors);
        const firstError = errorFields[0];

        // Focus the first invalid field
        setTimeout(() => {
          const fieldElement = document.querySelector(`[name="${firstError}"]`) as HTMLElement;
          if (fieldElement) {
            fieldElement.focus();
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);

        return;
      }

      const primaryPhysician = physicianData?.filter(
        (physician: any) => values?.primaryPhysician === physician?.uuid
      );

      // Get formatted priorCharges from the component
      const formattedPriorCharges = priorChargesRef.current?.getFormattedCharges() || [];

      const updatedData = {
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        dateOfBirth: getCurrentDateUTCWithoutTime(values.dateOfBirth || new Date()),
        primaryPhysician: primaryPhysician?.length > 0 ? primaryPhysician[0]?.physicianName : '',
        profileImage: file,
        gender: values.gender,
        physicianIds: [values.primaryPhysician],
        priorMedicareBCharges: formattedPriorCharges,
        medicalRecordNumber: values.medicalRecordNumber,
        bedAssignment: values.bedAssignment,
        socialSecurityNumber: values.socialSecurityNumber
          ? values.socialSecurityNumber.replace(/\D/g, '')
          : action === 'Edit' && !editSSNFocused && patientData?.socialSecurityNumber
          ? patientData.socialSecurityNumber.toString().replace(/\D/g, '')
          : null,
        floorNumber: values.floorNumber ? values.floorNumber : null,
        roomNumber: values.roomNumber ? values.roomNumber : null,
        patientEmail: values?.patientEmail,
        patientPhoneNo: formatContactNumber(values?.patientPhoneNo).cleanedValue,
        otTherapist: values.otTherapist,
        ptTherapist: values.ptTherapist,
        slpTherapist: values.slpTherapist,
      };
      if (action === 'Create') dispatch(createPatientAction(updatedData));
      else
        dispatch(
          editPatientAction({
            ...updatedData,
            uuid: patientId,
          })
        );
    },
  });

  useEffect(() => {
    if (action === 'Edit')
      dispatch(
        getSinglePatient({
          patientId: patientId,
        })
      );

    dispatch(resetEditPatientAction());
  }, []);

  useEffect(() => {
    if (!createPatientData.isPatientCreated && createPatientData.response === null) return;
    if (!createPatientData.isPatientCreated && createPatientData.response !== null) {
      setPatientError(createPatientData?.response?.message);
      setTimeout(() => {
        setPatientError('');
        dispatch(resetCreatePatientAction());
      }, 8000);
      return;
    }
    if (createPatientData?.isPatientCreated) setPatientError('');
    setTimeout(() => {
      dispatch(resetCreatePatientAction());

      if (createPatientData?.response?.data) {
        dispatch(getAllPatientsListResetAction());
        dispatch(getPatientIdAction(createPatientData?.response?.data));

        // Navigate back to the same place as breadcrumb/cancel
        const returnTo = state?.returnTo;
        if (returnTo?.path && returnTo?.state) {
          navigate(returnTo.path, { state: returnTo.state });
        } else {
          navigate(returnTo?.path || `${patients}/${patientDetails}`);
        }
        return;
      }
      dispatch(resetCreatePatientAction());

      // For create without data, go to same place as breadcrumb/cancel
      const returnTo = state?.returnTo;
      if (returnTo?.path && returnTo?.state) {
        navigate(returnTo.path, { state: returnTo.state });
      } else {
        navigate(returnTo?.path || `${patients}`);
      }
    }, 3000);
    return;
  }, [createPatientData]);

  useEffect(() => {
    if (!editPatientData.isPatientEdited && editPatientData.response === null) return;
    if (!editPatientData.isPatientEdited && editPatientData.response !== null) {
      setPatientError(editPatientData?.response?.message);
      setTimeout(() => {
        setPatientError('');
        dispatch(resetCreatePatientAction());
      }, 8000);
      return;
    }
    if (editPatientData?.isPatientEdited) setPatientError('');
    setTimeout(() => {
      dispatch(getAllPatientsListResetAction());
      dispatch(resetEditPatientAction());

      // Navigate back to the same place as breadcrumb/cancel
      const returnTo = state?.returnTo;
      if (returnTo?.path && returnTo?.state) {
        navigate(returnTo.path, { state: returnTo.state });
      } else {
        navigate(returnTo?.path || `${patients}/${patientDetails}`);
      }
    }, 3000);
    return;
  }, [editPatientData]);

  // Auto-focus first invalid field when coming from banner
  React.useEffect(() => {
    if (cameFromBanner && action === 'Edit') {
      setTimeout(() => {
        // Check if patient is missing required info
        const errors: any = {};

        if (!formik.values.dateOfBirth) {
          errors.dateOfBirth = true;
        }
        if (!formik.values.primaryPhysician) {
          errors.primaryPhysician = true;
        }

        if (Object.keys(errors).length > 0) {
          const firstError = Object.keys(errors)[0];
          const fieldElement = document.querySelector(`[name="${firstError}"]`) as HTMLElement;
          if (fieldElement) {
            fieldElement.focus();
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 500);
    }
  }, [cameFromBanner, action, formik.values.dateOfBirth, formik.values.primaryPhysician]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card>
        <CardHeader title={action === 'Edit' ? 'Edit Basic Details' : 'Add Basic Details'} />
        <CardContent sx={{ py: 0 }}>
          {showValidationErrors &&
            Object.keys(formik.errors).length > 0 &&
            !formik.errors?.priorCharges && (
              <Alert
                severity="error"
                sx={{ mb: 3 }}
              >
                <AlertTitle>Please fix the following errors:</AlertTitle>
                <Box
                  component="ul"
                  sx={{ pl: 2, mb: 0 }}
                >
                  {Object.entries(formik.errors).map(([field, error]) => (
                    <Box
                      component="li"
                      key={field}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        color="error"
                      >
                        {typeof error === 'string' ? error : 'This field is required'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Alert>
            )}
          <Grid
            container
            spacing={3}
            padding={'20px 0px'}
          >
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={shouldShowFieldError(
                  'firstName',
                  formik.values.firstName,
                  formik.errors.firstName
                )}
                fullWidth
                helperText={
                  (formik.touched.firstName || showValidationErrors) &&
                  (formik.errors.firstName as any)
                }
                label="First Name"
                name="firstName"
                inputProps={{ 'data-testid': patientFormTestId('first-name') }}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.firstName}
                required
                InputLabelProps={{
                  shrink:
                    formik.values.firstName !== '' && !formik.touched.firstName ? true : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={shouldShowFieldError(
                  'lastName',
                  formik.values.lastName,
                  formik.errors.lastName
                )}
                fullWidth
                helperText={
                  (formik.touched.lastName || showValidationErrors) &&
                  (formik.errors.lastName as any)
                }
                label="Last Name"
                name="lastName"
                inputProps={{ 'data-testid': patientFormTestId('last-name') }}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.lastName}
                required
                InputLabelProps={{
                  shrink:
                    formik.values.lastName !== '' && !formik.touched.lastName ? true : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={shouldShowFieldError('gender', formik.values.gender, formik.errors.gender)}
                fullWidth
                helperText={
                  (formik.touched.gender || showValidationErrors) &&
                  formik.errors.gender &&
                  typeof formik.errors.gender === 'string' &&
                  formik.errors.gender
                }
                label="Gender"
                name="gender"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.gender}
                required
                select
                SelectProps={{
                  SelectDisplayProps: {
                    'data-testid': patientFormTestId('gender'),
                  } as HTMLAttributes<HTMLDivElement>,
                }}
              >
                {categoryOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    data-testid={patientsOptionTestId(patientFormTestId('gender'), option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <DatePicker
                onChange={(newDate) => {
                  formik.setFieldValue('dateOfBirth', newDate);
                }}
                label="Date Of Birth"
                value={formik.values.dateOfBirth}
                format="MMM dd, yyyy"
                disableFuture
                slotProps={{
                  textField: {
                    required: true,
                    error: shouldShowFieldError(
                      'dateOfBirth',
                      formik.values.dateOfBirth,
                      formik.errors.dateOfBirth
                    ),
                    helperText:
                      (formik.touched.dateOfBirth || showValidationErrors) &&
                      (formik.errors.dateOfBirth as any),
                    name: 'dateOfBirth',
                    onBlur: formik.handleBlur,
                    inputProps: { 'data-testid': patientFormTestId('date-of-birth') },
                  },
                }}
                sx={{
                  width: '100%',
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.patientPhoneNo && formik.errors.patientPhoneNo)}
                fullWidth
                helperText={formik.touched.patientPhoneNo && (formik.errors.patientPhoneNo as any)}
                label="Phone Number"
                name="patientPhoneNo"
                onBlur={formik.handleBlur}
                onChange={(e) =>
                  formik.setFieldValue(
                    'patientPhoneNo',
                    formatContactNumber(e.target.value).formattedValue
                  )
                }
                value={formik.values.patientPhoneNo}
                InputLabelProps={{
                  shrink:
                    formik.values.patientPhoneNo !== '' && !formik.touched.patientPhoneNo
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.patientEmail && formik.errors.patientEmail)}
                fullWidth
                helperText={formik.touched.patientEmail && (formik.errors.patientEmail as any)}
                label="Email"
                name="patientEmail"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.patientEmail}
                InputLabelProps={{
                  shrink:
                    formik.values.patientEmail !== '' && !formik.touched.patientEmail
                      ? true
                      : undefined,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardHeader title={action === 'Edit' ? 'Edit Therapy Details' : 'Add Therapy Details'} />
        <CardContent sx={{ py: 0 }}>
          <Grid
            container
            spacing={3}
            padding={'20px 0px'}
          >
            <Grid
              xs={12}
              md={6}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                getOptionLabel={(option: any) => option?.physicianName || ''}
                onChange={(event, newValue: any) => {
                  formik.setFieldValue('primaryPhysician', newValue?.uuid || '');
                  setSelectedPhysician(newValue || null);
                }}
                filterOptions={(x) => x}
                // clearOnBlur={false}
                onOpen={() => {
                  setSearch('');
                  setPageNo(0);
                }}
                onClose={(event) => {
                  setSearch('');
                }}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                loading={physicianResponse?.loading}
                value={
                  physicianList?.find(
                    (physician: any) => physician.uuid === formik.values.primaryPhysician
                  ) || null
                }
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      data-testid={patientsOptionTestId(
                        patientFormTestId('primary-physician'),
                        option?.uuid
                      )}
                      {...props}
                    >
                      {option?.physicianName}
                    </MenuItem>
                  );
                }}
                ListboxProps={{
                  style: { maxHeight: '300px', overflow: 'auto' },
                  role: 'closeMenu',
                  onScroll: (event) => {
                    const scrollThreshold = 1;
                    if (
                      Math.abs(
                        (event.target as any).scrollHeight -
                          (event.target as any).scrollTop -
                          (event.target as any).clientHeight
                      ) <= scrollThreshold
                    ) {
                      loaderMorePhysician();
                    }
                  },
                }}
                options={physicianList || []}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {physicianResponse?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    error={shouldShowFieldError(
                      'primaryPhysician',
                      formik.values.primaryPhysician,
                      formik.errors.primaryPhysician
                    )}
                    helperText={
                      formik.touched.primaryPhysician && (formik.errors.primaryPhysician as any)
                    }
                    onChange={handleSearchInputChange}
                    label="Primary Physician"
                    required
                    name="primaryPhysician"
                    inputProps={{
                      ...params.inputProps,
                      'data-testid': patientFormTestId('primary-physician'),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                getOptionLabel={(option: any) => option?.name || ''}
                onChange={(event, newValue: any) => {
                  formik.setFieldValue('otTherapist', newValue?.uuid);
                }}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                loading={allTherapistDisciplineResponse?.loading}
                value={
                  allTherapistDisciplineResponse?.otResponse?.find(
                    (therapist: any) => therapist.uuid === formik.values.otTherapist
                  ) || null
                }
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      {...props}
                    >
                      {option?.name}
                    </MenuItem>
                  );
                }}
                options={allTherapistDisciplineResponse?.otResponse || []}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {allTherapistDisciplineResponse?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    label="Treating Therapist-OT"
                  />
                )}
              />
            </Grid>

            <Grid
              xs={12}
              md={6}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                getOptionLabel={(option: any) => option?.name || ''}
                onChange={(event, newValue: any) => {
                  formik.setFieldValue('ptTherapist', newValue?.uuid);
                }}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                loading={allTherapistDisciplineResponse?.loading}
                value={
                  allTherapistDisciplineResponse?.ptResponse?.find(
                    (therapist: any) => therapist.uuid === formik.values.ptTherapist
                  ) || null
                }
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      {...props}
                    >
                      {option?.name}
                    </MenuItem>
                  );
                }}
                options={allTherapistDisciplineResponse?.ptResponse || []}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {allTherapistDisciplineResponse?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    label="Treating Therapist-PT"
                  />
                )}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                getOptionLabel={(option: any) => option?.name || ''}
                onChange={(event, newValue: any) => {
                  formik.setFieldValue('slpTherapist', newValue?.uuid);
                }}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                loading={allTherapistDisciplineResponse?.loading}
                value={
                  allTherapistDisciplineResponse?.slpResponse?.find(
                    (therapist: any) => therapist.uuid === formik.values.slpTherapist
                  ) || null
                }
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      {...props}
                    >
                      {option?.name}
                    </MenuItem>
                  );
                }}
                options={allTherapistDisciplineResponse?.slpResponse || []}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {allTherapistDisciplineResponse?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    label="Treating Therapist-SLP"
                  />
                )}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.bedAssignment && formik.errors.bedAssignment)}
                fullWidth
                helperText={formik.touched.bedAssignment && (formik.errors.bedAssignment as any)}
                label="Bed Assignment"
                name="bedAssignment"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.bedAssignment}
                InputLabelProps={{
                  shrink:
                    formik.values.bedAssignment !== '' && !formik.touched.bedAssignment
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={shouldShowFieldError(
                  'medicalRecordNumber',
                  formik.values.medicalRecordNumber,
                  formik.errors.medicalRecordNumber
                )}
                fullWidth
                helperText={
                  (formik.touched.medicalRecordNumber || showValidationErrors) &&
                  (formik.errors.medicalRecordNumber as any)
                }
                label="Medical Record Number"
                name="medicalRecordNumber"
                inputProps={{ 'data-testid': patientFormTestId('medical-record-number') }}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.medicalRecordNumber}
                required
                InputLabelProps={{
                  shrink:
                    formik.values.medicalRecordNumber !== '' && !formik.touched.medicalRecordNumber
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              {(() => {
                const isCreate = action === 'Create';
                const rawValue = formik.values.socialSecurityNumber || '';
                const cleaned = rawValue.replace(/\D/g, '');
                const isEditWithMaskedPlaceholder =
                  !isCreate && !editSSNFocused && !!patientData?.socialSecurityNumber;

                const displayValue = isEditWithMaskedPlaceholder
                  ? maskSSNForDisplay(patientData?.socialSecurityNumber?.toString())
                  : // Create may reveal the full value via the eye toggle; Edit re-entry never reveals.
                  isCreate && showSSN
                  ? formatSSNPartial(rawValue)
                  : maskSSNForInputDisplay(rawValue);

                const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key >= '0' && e.key <= '9') {
                    e.preventDefault();
                    const next = (cleaned + e.key).slice(0, 9);
                    formik.setFieldValue('socialSecurityNumber', next, false);
                  } else if (e.key === 'Backspace') {
                    e.preventDefault();
                    formik.setFieldValue('socialSecurityNumber', cleaned.slice(0, -1), false);
                  }
                };

                const handlePaste = (e: React.ClipboardEvent) => {
                  e.preventDefault();
                  const pasted = (e.clipboardData?.getData('text') || '')
                    .replace(/\D/g, '')
                    .slice(0, 9);
                  formik.setFieldValue('socialSecurityNumber', pasted, false);
                };

                return (
                  <TextField
                    error={
                      !!(formik.touched.socialSecurityNumber && formik.errors.socialSecurityNumber)
                    }
                    fullWidth
                    helperText={
                      formik.touched.socialSecurityNumber &&
                      (formik.errors.socialSecurityNumber as any)
                    }
                    label="Social Security Number"
                    name="socialSecurityNumber"
                    type="text"
                    placeholder="XXX-XX-XXXX"
                    autoComplete="off"
                    value={displayValue}
                    // Entry is driven entirely by keydown/paste so only digits are ever stored.
                    onChange={() => {}}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onFocus={() => {
                      if (!isCreate && !editSSNFocused) {
                        setEditSSNFocused(true);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    inputProps={{
                      'data-form-type': 'other',
                      'data-lpignore': 'true',
                      'data-testid': patientFormTestId('social-security-number'),
                      inputMode: 'numeric',
                    }}
                    InputProps={{
                      ...(isCreate && {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={showSSN ? 'Hide SSN' : 'Show SSN'}
                              onClick={() => setShowSSN((prev) => !prev)}
                              edge="end"
                            >
                              {showSSN ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }),
                    }}
                    InputLabelProps={{
                      shrink: displayValue !== '' ? true : undefined,
                    }}
                  />
                );
              })()}
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.floorNumber && formik.errors.floorNumber)}
                fullWidth
                helperText={formik.touched.floorNumber && (formik.errors.floorNumber as any)}
                label="Floor Number"
                name="floorNumber"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.floorNumber}
                InputLabelProps={{
                  shrink:
                    formik.values.floorNumber !== '' && !formik.touched.floorNumber
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.roomNumber && formik.errors.roomNumber)}
                fullWidth
                helperText={formik.touched.roomNumber && (formik.errors.roomNumber as any)}
                label="Room Number"
                name="roomNumber"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.roomNumber}
                InputLabelProps={{
                  shrink:
                    formik.values.roomNumber !== '' && !formik.touched.roomNumber
                      ? true
                      : undefined,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>

        <PriorMedicareCharges
          ref={priorChargesRef}
          action={action as 'Create' | 'Edit'}
          patientData={patientData}
          formik={formik}
          showValidationErrors={showValidationErrors}
        />
        {(!createPatientData?.isPatientCreated || !editPatientData?.isPatientEdited) && (
          <Typography
            padding={'0px 20px'}
            variant="h6"
            color="red"
          >
            {patientError}
          </Typography>
        )}
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          flexWrap="wrap"
          justifyContent={'flex-end'}
          spacing={3}
          sx={{ p: 3 }}
        >
          <Button
            color="inherit"
            component={RouterLink}
            disabled={formik.isSubmitting}
            onClick={() => navigate(-1)}
            data-testid={patientFormTestId('cancel')}
          >
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || createPatientData?.loading || editPatientData?.loading}
            type="submit"
            variant="contained"
            data-testid={patientFormTestId('save')}
          >
            Save
          </Button>
        </Stack>
      </Card>
      {(createPatientData?.isPatientCreated || editPatientData.isPatientEdited) && (
        <SimpleSnackbar
          message={
            createPatientData?.isPatientCreated
              ? 'New Patient is added successfully'
              : 'Patient details are updated successfully!'
          }
        />
      )}
    </form>
  );
};
